import React, { useEffect, useState } from 'react';
import {
  makeStyles,
  Grid,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Box,
  CircularProgress,
} from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CodeIcon from '@material-ui/icons/Code';
import DescriptionIcon from '@material-ui/icons/Description';
import StorageIcon from '@material-ui/icons/Storage';
import ExtensionIcon from '@material-ui/icons/Extension';
import BuildIcon from '@material-ui/icons/Build';
import CategoryIcon from '@material-ui/icons/Category';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';

const useStyles = makeStyles(theme => ({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: '15px',
    border: '1px solid #E2E8F0',
    boxShadow: '0px 3.5px 5.5px 0px rgba(0,0,0,0.02)',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0px 8px 20px 0px rgba(0,0,0,0.08)',
    },
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  iconWrapper: {
    width: 44,
    height: 44,
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  icon: {
    fontSize: 24,
    color: '#FFFFFF',
  },
  title: {
    color: '#2D3748',
    fontSize: '16px',
    fontWeight: 700,
    lineHeight: 1.3,
  },
  description: {
    color: '#718096',
    fontSize: '13px',
    lineHeight: 1.5,
    flexGrow: 1,
    marginBottom: theme.spacing(2),
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  tagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  tag: {
    height: 22,
    fontSize: '10px',
    fontWeight: 600,
    borderColor: '#E2E8F0',
    color: '#718096',
  },
  kindChip: {
    height: 22,
    fontSize: '10px',
    fontWeight: 700,
    color: '#FFFFFF',
  },
  actions: {
    padding: theme.spacing(0, 2, 2, 2),
  },
  exploreButton: {
    color: '#2A5A4F',
    fontSize: '12px',
    fontWeight: 700,
    textTransform: 'none',
    padding: 0,
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
  arrowIcon: {
    fontSize: 14,
    marginLeft: theme.spacing(0.5),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
  empty: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: '#A0AEC0',
  },
}));

const KIND_COLORS: Record<string, string> = {
  component: '#2A5A4F',
  template: '#F67035',
  api: '#4299E1',
  resource: '#9F7AEA',
  system: '#ED64A6',
};

const KIND_ICONS: Record<string, React.ReactElement> = {
  component: <CodeIcon />,
  template: <BuildIcon />,
  api: <DescriptionIcon />,
  resource: <StorageIcon />,
  system: <ExtensionIcon />,
};

function getEntityUrl(entity: Entity): string {
  const kind = entity.kind.toLowerCase();
  const namespace = entity.metadata.namespace || 'default';
  const name = entity.metadata.name;
  return `/catalog/${namespace}/${kind}/${name}`;
}

function getEntityTags(entity: Entity): string[] {
  return (entity.metadata.tags || []).slice(0, 4);
}

function shuffle<T>(arr: T[]): T[] {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

interface AcceleratorsSectionProps {
  maxItems?: number;
}

export const AcceleratorsSection: React.FC<AcceleratorsSectionProps> = ({
  maxItems = 6,
}) => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { items } = await catalogApi.getEntities({
          filter: [
            { kind: 'Component' },
            { kind: 'Template' },
            { kind: 'API' },
            { kind: 'Resource' },
          ],
        });
        if (!cancelled) {
          setEntities(shuffle(items).slice(0, maxItems));
          setLoading(false);
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [catalogApi, maxItems]);

  if (loading) {
    return (
      <Box className={classes.loading}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (entities.length === 0) {
    return (
      <Typography className={classes.empty}>
        No accelerators found in the catalog.
      </Typography>
    );
  }

  return (
    <Grid container spacing={3}>
      {entities.map(entity => {
        const kind = entity.kind.toLowerCase();
        const color = KIND_COLORS[kind] || '#718096';
        const icon = KIND_ICONS[kind] || <CategoryIcon />;
        const tags = getEntityTags(entity);
        const description =
          entity.metadata.description || 'No description available.';

        return (
          <Grid item xs={12} sm={6} md={4} key={entity.metadata.uid}>
            <Card className={classes.card}>
              <CardContent>
                <Box className={classes.cardHeader}>
                  <Box
                    className={classes.iconWrapper}
                    style={{ backgroundColor: color }}
                  >
                    {React.cloneElement(icon, { className: classes.icon })}
                  </Box>
                  <Box>
                    <Typography className={classes.title}>
                      {entity.metadata.title || entity.metadata.name}
                    </Typography>
                  </Box>
                </Box>

                <Box className={classes.tagsContainer}>
                  <Chip
                    label={entity.kind}
                    size="small"
                    className={classes.kindChip}
                    style={{ backgroundColor: color }}
                  />
                  {Boolean((entity.spec as Record<string, unknown>)?.type) && (
                    <Chip
                      label={String(
                        (entity.spec as Record<string, unknown>).type,
                      )}
                      size="small"
                      variant="outlined"
                      className={classes.tag}
                    />
                  )}
                  {tags.map(tag => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      variant="outlined"
                      className={classes.tag}
                    />
                  ))}
                </Box>

                <Typography className={classes.description}>
                  {description}
                </Typography>
              </CardContent>
              <CardActions className={classes.actions}>
                <Button
                  className={classes.exploreButton}
                  href={getEntityUrl(entity)}
                  disableRipple
                >
                  Explore
                  <ArrowForwardIcon className={classes.arrowIcon} />
                </Button>
              </CardActions>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};
